import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const MigrationStories = () => {
  const [activeStory, setActiveStory] = useState(0);

  const stories = [
    {
      company: "TechCorp Manufacturing",
      industry: "Industrial Manufacturing",
      size: "15,000 employees",
      logo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop&crop=center",
      challenge: "Spending $2.8M annually on proprietary AI tools for predictive maintenance and quality control, with limited customization options and vendor lock-in concerns.",
      solution: "Migrated to open-source AI stack including TensorFlow, Apache Spark, and custom MLOps pipeline. Built internal AI capabilities with dedicated team.",
      results: {
        costSavings: "$2.1M annually",
        timeToMarket: "60% faster",
        customization: "Unlimited",
        roi: "340% in 18 months"
      },
      timeline: "8 months migration",
      quote: "Open-source AI didn't just save us money—it gave us the freedom to innovate at our own pace and build competitive advantages our competitors can't replicate.",
      executive: "Sarah Chen, CTO",
      metrics: [
        { label: "Annual Savings", value: "$2.1M", icon: "DollarSign", color: "text-green-500" },
        { label: "Deployment Speed", value: "60% Faster", icon: "Zap", color: "text-blue-500" },
        { label: "Custom Models", value: "25+ Built", icon: "Cpu", color: "text-purple-500" },
        { label: "ROI Timeline", value: "18 Months", icon: "TrendingUp", color: "text-orange-500" }
      ]
    },
    {
      company: "FinanceFirst Bank",
      industry: "Financial Services",
      size: "8,500 employees",
      logo: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=100&h=100&fit=crop&crop=center",
      challenge: "Regulatory compliance issues with cloud-based proprietary AI for fraud detection. High costs and limited control over data processing.",
      solution: "Implemented on-premises open-source AI infrastructure with custom fraud detection models. Full data sovereignty and compliance control.",
      results: {
        costSavings: "$1.8M annually",
        timeToMarket: "45% faster",
        customization: "Full control",
        roi: "280% in 24 months"
      },
      timeline: "12 months migration",
      quote: "Moving to open-source AI was the best strategic decision we made. We now have complete control over our data and can respond to new fraud patterns in hours, not months.",
      executive: "Michael Rodriguez, Chief Risk Officer",
      metrics: [
        { label: "Compliance Score", value: "100%", icon: "Shield", color: "text-green-500" },
        { label: "Detection Speed", value: "45% Faster", icon: "Clock", color: "text-blue-500" },
        { label: "False Positives", value: "70% Reduction", icon: "Target", color: "text-purple-500" },
        { label: "Data Control", value: "Complete", icon: "Lock", color: "text-orange-500" }
      ]
    },
    {
      company: "HealthTech Solutions",
      industry: "Healthcare Technology",
      size: "3,200 employees",
      logo: "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=100&h=100&fit=crop&crop=center",
      challenge: "Proprietary AI for medical imaging analysis was expensive and couldn\'t be customized for specialized diagnostic needs. HIPAA compliance concerns.",
      solution: "Built custom open-source AI pipeline for medical imaging with specialized models for rare conditions. Full HIPAA compliance and data control.",
      results: {
        costSavings: "$950K annually",
        timeToMarket: "80% faster",
        customization: "Specialized models",
        roi: "420% in 15 months"
      },
      timeline: "6 months migration",
      quote: "Open-source AI allowed us to build specialized diagnostic tools that simply weren't available from any vendor. We're now leading our field in AI-powered diagnostics.",
      executive: "Dr. Amanda Foster, Chief Medical Officer",
      metrics: [
        { label: "Diagnostic Accuracy", value: "94.7%", icon: "Activity", color: "text-green-500" },
        { label: "Processing Speed", value: "80% Faster", icon: "Zap", color: "text-blue-500" },
        { label: "Custom Models", value: "12 Specialized", icon: "Brain", color: "text-purple-500" },
        { label: "Patient Outcomes", value: "35% Better", icon: "Heart", color: "text-red-500" }
      ]
    }
  ];

  const currentStory = stories?.[activeStory];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-brand-primary mb-6">
            Real Migration Success Stories
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            See how leading enterprises successfully transitioned from proprietary to open-source AI, 
            achieving dramatic cost savings and competitive advantages.
          </p>
        </div>

        {/* Story Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {stories?.map((story, index) => (
            <button
              key={index}
              onClick={() => setActiveStory(index)}
              className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                activeStory === index
                  ? 'bg-brand-primary text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Image
                src={story?.logo}
                alt={`${story?.company} logo`}
                className="w-6 h-6 rounded mr-3"
              />
              <span className="hidden sm:inline">{story?.company}</span>
              <span className="sm:hidden">{story?.company?.split(' ')?.[0]}</span>
            </button>
          ))}
        </div>

        {/* Active Story */}
        <div className="bg-gray-50 rounded-2xl overflow-hidden shadow-executive">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-8 text-white">
            <div className="flex items-center mb-6">
              <Image
                src={currentStory?.logo}
                alt={`${currentStory?.company} logo`}
                className="w-16 h-16 rounded-lg mr-6 bg-white p-2"
              />
              <div>
                <h3 className="text-2xl font-bold">{currentStory?.company}</h3>
                <p className="text-blue-200">{currentStory?.industry} • {currentStory?.size}</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6">
              {currentStory?.metrics?.map((metric, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <Icon name={metric?.icon} size={24} className="text-white" />
                  </div>
                  <div className="text-2xl font-bold">{metric?.value}</div>
                  <div className="text-sm text-blue-200">{metric?.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Challenge & Solution */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-brand-primary mb-3 flex items-center">
                    <Icon name="AlertTriangle" size={20} className="mr-2" />
                    The Challenge
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{currentStory?.challenge}</p>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-brand-primary mb-3 flex items-center">
                    <Icon name="Lightbulb" size={20} className="mr-2" />
                    The Solution
                  </h4>
                  <p className="text-gray-700 leading-relaxed">{currentStory?.solution}</p>
                </div>

                <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <Icon name="Clock" size={16} className="text-brand-accent mr-2" />
                    <span className="font-semibold text-gray-900">Migration Timeline</span>
                  </div>
                  <span className="text-brand-accent font-bold">{currentStory?.timeline}</span>
                </div>
              </div>

              {/* Results & Quote */}
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-bold text-brand-primary mb-4 flex items-center">
                    <Icon name="TrendingUp" size={20} className="mr-2" />
                    Key Results
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="text-sm text-green-700 font-medium">Cost Savings</div>
                      <div className="text-xl font-bold text-green-600">{currentStory?.results?.costSavings}</div>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm text-blue-700 font-medium">Speed Improvement</div>
                      <div className="text-xl font-bold text-blue-600">{currentStory?.results?.timeToMarket}</div>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="text-sm text-purple-700 font-medium">Customization</div>
                      <div className="text-xl font-bold text-purple-600">{currentStory?.results?.customization}</div>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="text-sm text-orange-700 font-medium">ROI Achievement</div>
                      <div className="text-xl font-bold text-orange-600">{currentStory?.results?.roi}</div>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start">
                    <Icon name="Quote" size={24} className="text-brand-primary mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <p className="text-gray-700 italic mb-4 leading-relaxed">
                        "{currentStory?.quote}"
                      </p>
                      <div className="font-semibold text-brand-primary">
                        {currentStory?.executive}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-brand-primary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Write Your Success Story?
            </h3>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              Join these industry leaders who chose open-source AI for strategic advantage. 
              Our proven migration methodology ensures your success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-brand-accent hover:bg-brand-accent/90 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
                Get Migration Strategy
              </button>
              <button className="border border-white text-white hover:bg-white hover:text-brand-primary px-8 py-3 rounded-lg font-semibold transition-colors duration-200">
                View All Case Studies
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MigrationStories;